import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import ButtonBase from 'atoms/buttons/ButtonBase';
import CloseButton from 'atoms/buttons/CloseButton';
import { Horizontal } from 'atoms/layout/flex';
import { BodyS } from 'atoms/typography';
import DiscountBannerWrapper from 'components/referral/DiscountBannerWrapper';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { useSportContext } from 'contexts/sport';
import { useWalletDrawerContext } from 'contexts/walletDrawer';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';
import useAmountWithConversion from 'hooks/useAmountWithConversion';
import { useConversionCredit } from 'hooks/useConversionCredit';
import useLifecycle, { LIFECYCLE, Lifecycle } from 'hooks/useLifecycle';
import { zeroMonetaryAmount } from 'hooks/useMonetaryAmount';
import { glossary } from 'lib/glossary';

import { TermsLink } from '../DumbConversionCreditBanner/ui';
import { TermsDialog } from '../TermsDialog';

const messages = defineMessages({
  haveDiscountCredits: {
    id: 'ConversionCreditBanner.haveDiscountCredits',
    defaultMessage:
      'Your {percentageDiscount}% Credit boost (up to { maxDiscount }) is expiring in just {timeLeft}! Take a peek <walletLink>in your wallet</walletLink> to view all your available credits.',
  },
  wonDiscountCredits: {
    id: 'ConversionCreditBanner.wonDiscountCredits',
    defaultMessage:
      'You have won <span>{maxDiscount} credits</span> to use on {sport} <link1>auctions</link1> or <link2>instant buy</link2>.',
  },
  wonDiscountCreditsUpTo: {
    id: 'ConversionCreditBanner.wonDiscountCreditsUpTo',
    defaultMessage:
      'You have won <span>{percentageDiscount}% credits (up to { maxDiscount })</span> to use on {sport} <link1>auctions</link1> or <link2>instant buy</link2>.',
  },
});

type Props = {
  sport?: Sport;
  rounded?: boolean;
};

const WalletLink = styled(ButtonBase)`
  text-decoration: underline;
`;

export const ConversionCreditBanner = ({ sport }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { formatDistanceToNow } = useIntlContext();
  const sportContext = useSportContext();
  const { toggleDrawer } = useWalletDrawerContext();
  const { currentUser } = useCurrentUserContext();
  const conversionCredit = useConversionCredit(sport || sportContext?.sport);
  const { main } = useAmountWithConversion({
    monetaryAmount: conversionCredit?.maxDiscount || zeroMonetaryAmount,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const { update } = useLifecycle();
  const hideConversionCreditBanner = (
    currentUser?.userSettings?.lifecycle as Lifecycle
  )?.hideConversionCreditBanner;

  if (
    currentUser?.userSettings?.hideBalance ||
    hideConversionCreditBanner ||
    !conversionCredit
  )
    return null;

  const { endDate, percentageDiscount } = conversionCredit;

  const timeLeft = formatDistanceToNow(endDate, { addSuffix: false });

  return (
    <DiscountBannerWrapper
      rightNode={
        isTabletAndAbove && (
          <Horizontal>
            <CloseButton
              small
              onClose={() => {
                update(LIFECYCLE.hideConversionCreditBanner, true);
              }}
            />
          </Horizontal>
        )
      }
    >
      <BodyS>
        <FormattedMessage
          {...messages.haveDiscountCredits}
          values={{
            maxDiscount: main,
            percentageDiscount: percentageDiscount * 100,
            timeLeft,
            walletLink: (...chunks) => (
              <WalletLink
                onClick={() => {
                  toggleDrawer();
                }}
              >
                {chunks}
              </WalletLink>
            ),
          }}
        />{' '}
        <TermsLink type="button" onClick={() => setOpenDialog(true)}>
          (
          <FormattedMessage {...glossary.termsApply} />)
        </TermsLink>
      </BodyS>
      {openDialog && <TermsDialog onClose={() => setOpenDialog(false)} />}
    </DiscountBannerWrapper>
  );
};
